import { Paper as MuiPaper, PaperProps as MuiPaperProps, useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material';
import { IconButton } from '../button';

const theme = (isMobile: boolean) =>
  createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: '2.8rem',
            border: 'solid 0.1rem var(--gray-50)',
            borderRadius: '0.6rem',
            boxShadow: 'none',

            backgroundColor: 'var(--white)',

            '&.MuiPaper-root': {
              overflowY: 'overlay',
            },

            '&[data-position=absolute]': {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: '70rem',
            },

            '&[data-size=default]': {
              width: '54rem',
            },
            '&[data-size=max-content]': {
              width: 'max-content',
              height: 'max-content',
            },
            '&[data-size=full-size]': {
              width: '100%',
              height: '100%',
            },
            ...(isMobile && {
              padding: 'none',
              border: 'none',
              backgroundColor: 'transparent',

              '&[data-size=default]': {
                width: '100%',
              },
            }),
            '&[data-size=min-content]': {
              maxWidth: 'calc(100% - (var(--spacing-6) * 2))',
              width: 'max-content',
              padding: 'var(--spacing-6)',
              paddingBottom: 0,

              border: 'solid 0.1rem var(--gray-50)',
              backgroundColor: 'var(--white)',

              '& > div:nth-of-type(1)': {
                gridRowGap: 'var(--spacing-6)',
                height: '100%',
              },
              '& > div:nth-of-type(1) > *:last-child': {
                paddingBottom: 'var(--spacing-6)',
              },
            },

            '& .MuiCloseIcon': {
              position: 'absolute',
              top: '0.4rem',
              right: '0.4rem',
            },
          },
        },
      },
    },
  });

export type PaperSizeProps = 'default' | 'max-content' | 'full-size' | 'min-content';

export interface PaperProps extends MuiPaperProps {
  paperSize?: PaperSizeProps;
  position?: 'absolute' | 'initial';
  onClose?: () => void;
}

export const Paper: React.FC<PaperProps> = ({
  paperSize = 'default',
  position = 'initial',
  children,
  onClose,
  ...props
}: PaperProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <ThemeProvider theme={theme(isMobile)}>
      <MuiPaper data-size={paperSize} data-position={position} {...props}>
        {children}
        {onClose && (
          <IconButton className={'MuiCloseIcon'} onClick={onClose} iconColor={'--gray-70'}>
            <CloseRoundedIcon />
          </IconButton>
        )}
      </MuiPaper>
    </ThemeProvider>
  );
};
